import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import config from '../config';
import { useAuth } from './AuthContext';

// Create a context for Socket.IO
const SocketContext = createContext();

/**
 * Socket.IO Provider Component
 * Establishes and manages the Socket.IO connection
 */
export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const { token } = useAuth();
  
  useEffect(() => {
    // Only connect if user is authenticated
    if (token) {
      
      // Initialize Socket.IO connection
      // Force WebSocket-only transport to avoid polling.
      // The default (polling → WebSocket upgrade) fails in multi-pod deployments because
      // each polling HTTP request may hit a different pod, causing the pod that did not
      // create the session to return 400.  With a persistent WebSocket connection there
      // are no subsequent HTTP requests, so sticky sessions are not required.
      const socketIo = io(config.apiUrl, {
        auth: { token },
        transports: ['websocket'],
        upgrade: false,
      });
      
      // Set up event handlers
      socketIo.on('connect', () => {
        setConnected(true);
      });
      
      socketIo.on('disconnect', () => {
        setConnected(false);
      });
      
      socketIo.on('connect_error', (error) => {
        console.error('Socket.IO connection error:', error);
        setConnected(false);
      });
      
      // Log all incoming events for debugging
      const onevent = socketIo.onevent;
      socketIo.onevent = function(packet) {
        const args = packet.data || [];
        onevent.call(this, packet);
      };
      
      // Store the socket instance
      setSocket(socketIo);
      
      // Cleanup on unmount
      return () => {
        socketIo.disconnect();
      };
    } else {
    }
    
    return () => {
      // No cleanup needed if socket wasn't initialized
    };
  }, [token]);
  
  return (
    <SocketContext.Provider value={{ socket, connected }}>
      {children}
    </SocketContext.Provider>
  );
};

/**
 * Custom hook to use the Socket.IO context
 * @returns {Object} Socket.IO context
 */
export const useSocket = () => useContext(SocketContext);

export default SocketContext; 